<app-feature-wrapper
  [childComponent]="alertingComponent"
  featureName="alerting"></app-feature-wrapper>

<ng-template #alertingComponent>
  <div class="flex flex-col gap-6">
    <div class="flex min-w-0 flex-col gap-1">
      <h1 class="font-brand text-3xl font-normal tracking-tight text-zinc-50">
        {{ 'pages.settings.alerting.header' | transloco }}
      </h1>
      <p class="text-sm tracking-tight text-zinc-400">
        {{ 'pages.settings.alerting.description' | transloco }}
      </p>
    </div>

    <section class="flex flex-col gap-4">
      <div class="flex flex-wrap items-center justify-between gap-4">
        <h2 class="text-xl font-medium tracking-tight text-zinc-50">
          {{ 'pages.settings.alerting.processors.processorsTitle' | transloco }}
        </h2>
        <span [vRequiredRoles]="['alertprocessors.manager']">
          <!-- tooltip on the wrapper: a disabled button swallows pointer events -->
          <span
            [vxTooltip]="
              hasAlertProcessorsQuota()
                ? undefined
                : ('misc.quotaReached' | transloco)
            "
            vxTooltipSide="bottom">
            <vx-button
              [disabled]="!hasAlertProcessorsQuota()"
              (click)="goToCreateAlertProcessor()">
              <vx-icon name="plus" size="sm" />
              {{
                'pages.settings.alerting.processors.createAlertProcessorBtn'
                  | transloco
              }}
            </vx-button>
          </span>
        </span>
      </div>

      <vx-card>
        @if (alertProcessors().length > 0) {
          <!-- column labels mirror the row layout below: same grid tracks,
               same gap, and the same trailing chevron lane -->
          <div class="flex items-center gap-6 px-6 pt-4 pb-3">
            <div
              class="grid min-w-0 flex-1 items-center gap-6"
              [style.grid-template-columns]="processorColumnTemplate">
              <span [class]="columnLabelClasses">
                {{
                  'pages.settings.alerting.processors.table.nameColumn'
                    | transloco
                }}
              </span>
              <span [class]="columnLabelClasses">
                {{
                  'pages.settings.alerting.processors.table.statusColumn'
                    | transloco
                }}
              </span>
              <span [class]="columnLabelClasses">
                {{
                  'pages.settings.alerting.processors.totalAlertsProcessed'
                    | transloco
                }}
              </span>
              <span [class]="columnLabelClasses">
                {{
                  'pages.settings.alerting.processors.lastAlertProcessed'
                    | transloco
                }}
              </span>
            </div>
            <span class="w-8 shrink-0"></span>
          </div>

          <vx-panel variant="list">
            @for (processor of alertProcessors(); track processor.id) {
              <vx-list-row
                [link]="['/settings/alerting/processors', processor.id]"
                [columnTemplate]="processorColumnTemplate">
                <span class="flex min-w-0 items-center gap-2">
                  @if (processor.isDefault) {
                    <vx-icon
                      name="star"
                      size="sm"
                      class="shrink-0 text-zinc-300"
                      [vxTooltip]="
                        'pages.settings.alerting.processors.defaultTooltip'
                          | transloco
                      " />
                  }
                  <span
                    class="truncate text-sm font-medium tracking-tight text-zinc-100">
                    {{ processor.name }}
                  </span>
                </span>

                <span>
                  <vx-badge tone="success">
                    {{
                      'pages.settings.alerting.processors.enabled' | transloco
                    }}
                  </vx-badge>
                </span>

                <span class="truncate text-sm tracking-tight text-zinc-400">
                  {{ processor.statistics.totalAlertsProcessed }}
                </span>

                <span class="truncate text-sm tracking-tight text-zinc-400">
                  @if (
                    processor.statistics.lastAlertProcessedAt;
                    as processedAt
                  ) {
                    {{
                      processedAt
                        | intlRelativeTime
                          : {
                              locale: currentLocale(),
                              numeric: 'auto',
                              style: 'short',
                            }
                    }}
                  } @else {
                    <span aria-hidden="true">—</span>
                  }
                </span>

                <span vxRowActions class="flex w-8 shrink-0 justify-end">
                  <vx-icon
                    name="caret-right"
                    size="sm"
                    class="text-zinc-500"
                    aria-hidden="true" />
                </span>
              </vx-list-row>
            }
          </vx-panel>
        } @else {
          <!-- the top inset normally comes from a card header; this card has
               none when empty, so restore it for a symmetric 6px float -->
          <div class="pt-1.5">
            <vx-empty-state
              icon="siren"
              [title]="
                'pages.settings.alerting.processors.noAlertProcessorsConfigured'
                  | transloco
              " />
          </div>
        }
      </vx-card>
    </section>

    <section class="flex flex-col gap-4">
      <div class="flex flex-wrap items-center justify-between gap-4">
        <h2 class="text-xl font-medium tracking-tight text-zinc-50">
          {{ 'pages.settings.alerting.processors.sourcesTitle' | transloco }}
        </h2>
        <span [vRequiredRoles]="['alertsources.manager']">
          <span
            [vxTooltip]="
              hasAlertSourcesQuota()
                ? undefined
                : ('misc.quotaReached' | transloco)
            "
            vxTooltipSide="bottom">
            <vx-button
              [disabled]="!hasAlertSourcesQuota()"
              (click)="goToCreateAlertSource()">
              <vx-icon name="plus" size="sm" />
              {{
                'pages.settings.alerting.sources.createAlertSourceBtn'
                  | transloco
              }}
            </vx-button>
          </span>
        </span>
      </div>

      <vx-card>
        @if (alertSources().length > 0) {
          <!-- column labels mirror the row layout below: same grid tracks,
               same gap, and the same trailing chevron lane -->
          <div class="flex items-center gap-6 px-6 pt-4 pb-3">
            <div
              class="grid min-w-0 flex-1 items-center gap-6"
              [style.grid-template-columns]="columnTemplate">
              <span [class]="columnLabelClasses">
                {{
                  'pages.settings.alerting.sources.table.typeColumn' | transloco
                }}
              </span>
              <span [class]="columnLabelClasses">
                {{
                  'pages.settings.alerting.sources.table.nameColumn' | transloco
                }}
              </span>
              <span [class]="columnLabelClasses">
                {{
                  'pages.settings.alerting.sources.table.processorColumn'
                    | transloco
                }}
              </span>
            </div>
            <span class="w-8 shrink-0"></span>
          </div>

          <vx-panel variant="list">
            @for (item of alertSources(); track item.source.id) {
              <vx-list-row
                [link]="['/settings/alerting/sources', item.source.id]"
                [columnTemplate]="columnTemplate">
                <vx-item-cell [title]="item.type.label">
                  <span
                    vxItemCellLeading
                    class="flex size-9 shrink-0 items-center justify-center rounded-lg bg-zinc-800">
                    <img
                      class="size-5"
                      [src]="item.type.icon"
                      [alt]="item.type.label" />
                  </span>
                </vx-item-cell>

                <span class="truncate text-sm tracking-tight text-zinc-400">
                  {{ item.source.name }}
                </span>

                <span class="truncate text-sm tracking-tight text-zinc-400">
                  {{ item.source.processor?.name }}
                </span>

                <span vxRowActions class="flex w-8 shrink-0 justify-end">
                  <vx-icon
                    name="caret-right"
                    size="sm"
                    class="text-zinc-500"
                    aria-hidden="true" />
                </span>
              </vx-list-row>
            }
          </vx-panel>
        } @else {
          <!-- the top inset normally comes from a card header; this card has
               none when empty, so restore it for a symmetric 6px float -->
          <div class="pt-1.5">
            <vx-empty-state
              icon="plugs"
              [title]="
                'pages.settings.alerting.sources.noAlertSourcesConfigured'
                  | transloco
              " />
          </div>
        }
      </vx-card>
    </section>
  </div>
</ng-template>
